/*
* === ApplicationFormPage.js (Final - Strict Validation & Prefill) ===
*/
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
// 🚨 NEW IMPORTS for Multi-Tenancy Form Routing
import { tenantConfig } from '../../config/tenantConfig';
import ApplicationFormJBMarks from '../../components/tenants/jbmarks/ApplicationFormJBMarks';
import { 
    Container, Box, Stepper, Step, StepLabel, Button, Typography, 
    CircularProgress, Paper, Alert, Snackbar 
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const steps = [
    'Personal Details', 
    'Guardian Details', 
    'Address', 
    'Financial Info', 
    'Academic Info', 
    'Document Upload', 
    'Review'
];

const stepRoutes = ['personal', 'guardian', 'address', 'financial', 'academic', 'documents', 'review'];

const ApplicationFormPage = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const fundId = searchParams.get('fundId');

    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({
        personal: {}, guardian: {}, address: {}, financial: {}, 
        academic: {}, documents: [], motivation: '', declaration: false 
    });
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false); 
    const [lastSaved, setLastSaved] = useState(null);
    
    // Strict error tracking state
    const [errors, setErrors] = useState({});
    
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const autoSaveTimerRef = useRef(null);

    // Sync active step with URL
    useEffect(() => {
        const path = location.pathname.split('/').pop();
        const currentPath = path === 'form' ? 'personal' : path;
        const stepIndex = stepRoutes.indexOf(currentPath);
        if (stepIndex !== -1) setActiveStep(stepIndex);
        else setActiveStep(0);
    }, [location]);

    // Initialize Data & Prefill from Profile
    useEffect(() => {
        const init = async () => {
            if (!token || !fundId) return;
            try {
                setLoading(true);
                const [profileRes, appRes, guardianRes, addressRes] = await Promise.all([
                    api.get('/applicant/profile', { headers: { Authorization: `Bearer ${token}` } }),
                    api.get(`/applicant/application?fundId=${fundId}`, { headers: { Authorization: `Bearer ${token}` } }),
                    // Fetch external profile tables for pre-fill
                    api.get('/applicant/guardian', { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
                    api.get('/applicant/address', { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
                ]);

                const profile = profileRes.data || {};
                const gData = guardianRes.data || {};
                const aData = addressRes.data || {};
                setProfileData(profile);

                let initialData = {
                    personal: {}, address: {}, academic: {}, financial: {}, 
                    guardian: {}, motivation: '', documents: [], declaration: false 
                };

                // If application already exists, parse it
                if (appRes.data) {
                    const { PersonalDetails, AcademicInfo, FinancialInfo, Motivation, Id } = appRes.data;
                    const parsedPersonal = typeof PersonalDetails === 'string' ? JSON.parse(PersonalDetails) : PersonalDetails || {};
                    const parsedAcademic = typeof AcademicInfo === 'string' ? JSON.parse(AcademicInfo) : AcademicInfo || {};
                    const parsedFinancial = typeof FinancialInfo === 'string' ? JSON.parse(FinancialInfo) : FinancialInfo || {};
                    
                    initialData = {
                        personal: parsedPersonal,
                        address: parsedPersonal.address || {},
                        academic: parsedAcademic,
                        financial: parsedFinancial.financial || parsedFinancial, 
                        guardian: parsedFinancial.guardian || {},
                        motivation: Motivation || '',
                        documents: [], 
                        declaration: false,
                        applicationId: Id
                    };
                }

                // Force inject core profile data
                initialData.personal = {
                    ...initialData.personal,
                    Surname: profile.Surname,
                    FirstNames: profile.FirstNames,
                    IdNumber: profile.IdNumber,
                    CellNumber: profile.CellNumber,
                    Gender: profile.Gender,
                    Race: profile.Race,
                    Province: profile.Province
                };

                // Auto-fill Guardian & Address if the application fields are currently empty
                if (Object.keys(initialData.guardian).length === 0) {
                    initialData.guardian = {
                        relationToApplicant: gData.Relation || '',
                        firstNames: gData.FirstName || '',
                        cellNo: gData.CellNumber || '',
                        email: gData.Email || '',
                        company: gData.Employer || ''
                    };
                }
                if (Object.keys(initialData.address).length === 0) {
                    initialData.address = {
                        physicalStreet: aData.Street || '',
                        physicalSuburb: aData.Suburb || '',
                        physicalCity: aData.City || '',
                        physicalCode: aData.PostalCode || ''
                    };
                }

                setFormData(initialData);
            } catch (err) {
                console.error("Init error:", err);
                setSnackbar({ open: true, message: 'Error loading application.', severity: 'error' });
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [token, fundId]);

    const saveDraft = useCallback(async (dataToSave = null) => {
        if (!fundId) return false;
        setSaving(true);
        try {
            const payload = dataToSave || formData; 
            const res = await api.post('/applicant/application', {
                formData: payload, isSubmitting: false, fundId
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            setLastSaved(new Date());
            if (res.data?.applicationId) {
                setFormData(prev => ({ ...prev, applicationId: res.data.applicationId }));
            }
            return true;
        } catch (err) {
            console.error("Save failed:", err);
            // 🚨 CRITICAL FIX: Tell the user exactly why the backend rejected the save
            const errorMsg = err.response?.data?.message || 'Database rejected the save.';
            setSnackbar({ open: true, message: `Save Error: ${errorMsg}`, severity: 'error' });
            return false;
        } finally {
            setSaving(false);
        }
    }, [fundId, token, formData]);

    const handleInputChange = (section, e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;

        setFormData(prev => {
            const updated = { 
                ...prev, 
                [section]: section === 'motivation' || section === 'declaration' ? val : { ...prev[section], [name]: val } 
            };
            
            // Safe auto-save using the most recent state
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = setTimeout(() => {
                saveDraft(updated);
            }, 2000);

            return updated;
        });

        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    // STRICT GATEKEEPER VALIDATION
    const handleNext = async () => {
        let currentErrors = {};
        let isValid = true;

        switch (activeStep) {
            case 0: // Personal Details
                if (!formData.personal?.email) { currentErrors.email = "Contact Email is required"; isValid = false; }
                break;
            case 1: // Guardian Details
                const g = formData.guardian || {};
                if (!g.relationToApplicant) { currentErrors.relationToApplicant = "Required"; isValid = false; }
                if (!g.surname) { currentErrors.surname = "Required"; isValid = false; }
                if (!g.firstNames) { currentErrors.firstNames = "Required"; isValid = false; }
                if (!g.idNumber) { currentErrors.idNumber = "Required"; isValid = false; }
                if (!g.company) { currentErrors.company = "Required"; isValid = false; }
                if (!g.industry) { currentErrors.industry = "Required"; isValid = false; }
                break;
            case 2: // Address Details
                const a = formData.address || {};
                if (!a.physicalStreet || !a.physicalCity || !a.physicalCode) { 
                    setSnackbar({ open: true, message: 'Please complete all required Residential Address fields.', severity: 'error' });
                    isValid = false; 
                }
                break;
            case 3: // Financial Details
                const f = formData.financial || {};
                if (!f.householdIncome) { currentErrors.householdIncome = "Required"; isValid = false; }
                if (!f.dependents) { currentErrors.dependents = "Required"; isValid = false; }
                if (!f.fundingSource) { currentErrors.fundingSource = "Required"; isValid = false; }
                break;
            case 4: // Academic Details
                const ac = formData.academic || {};
                if (!ac.currentStatus) { currentErrors.currentStatus = "Required"; isValid = false; }
                if (!ac.intendedCourse) { currentErrors.intendedCourse = "Required"; isValid = false; }
                if (!ac.intendedInstitution) { currentErrors.intendedInstitution = "Required"; isValid = false; }
                if (!ac.intendedQualification) { currentErrors.intendedQualification = "Required"; isValid = false; }
                if (!formData.motivation) { currentErrors.motivation = "Motivation is required"; isValid = false; }
                break;
            case 5: // Documents (Managed internally by DocumentUploadStep, but we check final flag here)
                if (errors.documents) { 
                    setSnackbar({ open: true, message: 'Please attach all required documents before proceeding.', severity: 'error' });
                    isValid = false; 
                }
                break;
            default:
                break;
        }

        if (!isValid) {
            setErrors(prev => ({ ...prev, ...currentErrors }));
            if (Object.keys(currentErrors).length > 0) {
                setSnackbar({ open: true, message: 'Please complete all required fields.', severity: 'error' });
            }
            return; // STOP THE USER FROM ADVANCING
        }

        // 🚨 CRITICAL FIX: We MUST await the save before navigating!
        const saveSuccess = await saveDraft(formData);
        if (!saveSuccess) {
            // The catch block in saveDraft already popped the error snackbar
            return; 
        }

        // If valid, save the draft to ensure DB is updated, clear errors, and proceed
        saveDraft(formData);
        setErrors({});
        
        if (activeStep === steps.length - 1) {
            handleSubmitApplication();
        } else {
            const nextPath = stepRoutes[activeStep + 1];
            navigate(`${nextPath}?fundId=${fundId}`);
        }
    };

    const handleSubmitApplication = async () => {
        if (!formData.declaration) {
            setSnackbar({ open: true, message: 'Please confirm the declaration.', severity: 'warning' });
            return;
        }

        setSubmitting(true);
        try {
            await api.post('/applicant/application', {
                formData, isSubmitting: true, fundId
            }, { headers: { Authorization: `Bearer ${token}` } });
            navigate('/applicant/dashboard');
        } catch (err) {
            setSnackbar({ open: true, message: 'Submission failed.', severity: 'error' });
            setSubmitting(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="bold">Application Form</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {saving && <Typography variant="caption">Saving...</Typography>}
                    {lastSaved && !saving && <Typography variant="caption" color="success.main">Saved {lastSaved.toLocaleTimeString()}</Typography>}
                    <Button startIcon={<SaveIcon />} onClick={() => saveDraft(formData)} variant="outlined" size="small" disabled={saving}>Save Draft</Button>
                </Box>
            </Paper>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
            </Stepper>

            <Paper sx={{ p: 4, minHeight: '400px' }}>
                <Outlet context={{ 
                    formData, handleInputChange, setFormData, 
                    errors, setErrors, 
                    profileData, 
                    applicationId: formData.applicationId, saveDraft, saving,
                    showSnackbar: (msg, sev) => setSnackbar({ open: true, message: msg, severity: sev || 'success' })
                }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                    <Button disabled={activeStep === 0 || submitting} onClick={() => {
                        const prevPath = stepRoutes[activeStep - 1];
                        navigate(`${prevPath}?fundId=${fundId}`);
                    }}>Back</Button>
                    <Button variant="contained" onClick={handleNext} disabled={submitting} endIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}>
                        {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
                    </Button>
                </Box>
            </Paper>
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

export default ApplicationFormPage;